import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmailPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendStatus, setResendStatus] = useState("idle"); // idle | sending | sent | error
  const [resendMsg, setResendMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Invalid verification link.");
      return;
    }
    const verify = async () => {
      try {
        const res = await axios.post(`${API}/auth/verify-email`, { token });
        setStatus("success");
        setMessage(res.data?.message || "Email verified successfully.");
      } catch (e) {
        setStatus("error");
        setMessage(e.response?.data?.detail || "Verification failed. The link may be expired.");
      }
    };
    verify();
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendStatus("sending");
    setResendMsg("");
    try {
      const res = await axios.post(`${API}/auth/resend-verification`, { email: resendEmail });
      setResendStatus("sent");
      setResendMsg(res.data?.message || "If an unverified account exists for that email, a new link has been sent.");
    } catch (err) {
      setResendStatus("error");
      setResendMsg(err.response?.data?.detail || "Could not resend. Try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-zinc-950 border-2 border-zinc-800 p-8 text-center space-y-5" data-testid="verify-email-page">
        <Link to="/" className="inline-block">
          <img src="/logo-master.png" alt="Pivot Performance" className="h-12 w-12 mx-auto object-contain" />
        </Link>

        {status === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto" />
            <h1 className="font-heading font-black uppercase text-2xl tracking-tight text-white" data-testid="verify-loading-heading">
              Verifying your email...
            </h1>
            <p className="text-sm text-zinc-400">Just a moment.</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="font-heading font-black uppercase text-2xl tracking-tight text-white" data-testid="verify-success-heading">
              Email Verified.
            </h1>
            <p className="text-sm text-zinc-400">{message} You're locked in.</p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full bg-orange-600 hover:bg-orange-500 text-white font-heading font-black uppercase tracking-wider h-12 btn-tactical"
              data-testid="verify-go-dashboard-btn"
            >
              Go to Dashboard →
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h1 className="font-heading font-black uppercase text-2xl tracking-tight text-white" data-testid="verify-error-heading">
              Verification Failed
            </h1>
            <p className="text-sm text-zinc-400">{message}</p>

            <div className="pt-4 border-t border-zinc-800 text-left space-y-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
                <Mail className="w-4 h-4 text-orange-500" />
                Resend Verification Link
              </div>
              {resendStatus === "sent" ? (
                <p className="text-sm text-emerald-400" data-testid="resend-success-msg">{resendMsg}</p>
              ) : (
                <form onSubmit={handleResend} className="space-y-2">
                  <Input
                    type="email"
                    required
                    placeholder="your@email.com"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-white h-11"
                    data-testid="resend-verification-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={resendStatus === "sending"}
                    className="w-full bg-orange-600 hover:bg-orange-500 text-white font-heading font-black uppercase tracking-wider h-11"
                    data-testid="resend-verification-submit-btn"
                  >
                    {resendStatus === "sending" ? "Sending..." : "Send New Link"}
                  </Button>
                  {resendStatus === "error" && (
                    <p className="text-xs text-red-400">{resendMsg}</p>
                  )}
                </form>
              )}
            </div>

            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="w-full bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800 font-heading font-black uppercase tracking-wider h-12"
              data-testid="verify-back-login-btn"
            >
              Back to Login
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
